import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// ─── Theme ────────────────────────────────────────────────────────────────────
const T = {
  bg: "#0b0f1a", sidebar: "#0f172a", card: "#0c1220", cardAlt: "#111827",
  accent: "#0D66D3", accentDim: "rgba(13,102,211,0.15)", accentBorder: "rgba(28,139,250,0.35)",
  border: "rgba(255,255,255,0.06)", borderMid: "rgba(255,255,255,0.10)",
  text: "#e5e7eb", muted: "#9ca3af", faint: "#6b7280",
  orange: "#f97316", red: "#ef4444", blue: "#60a5fa",
  green: "#22c55e", greenDim: "rgba(34,197,94,0.12)", greenBorder: "rgba(34,197,94,0.3)",
};

// ─── Password Strength ────────────────────────────────────────────────────────
function getStrength(val) {
  if (!val) return 0;
  let score = 0;
  if (val.length >= 8)          score++;
  if (/[A-Z]/.test(val))        score++;
  if (/[0-9]/.test(val))        score++;
  if (/[^A-Za-z0-9]/.test(val)) score++;
  return score;
}
const STRENGTH_LABELS = ["", "Weak", "Fair", "Good", "Strong"];
const STRENGTH_COLORS = ["", T.red, T.orange, T.blue, T.accent];

// ─── Form Input ───────────────────────────────────────────────────────────────
function FormInput({ label, type = "text", placeholder, value, onChange, extra }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: "block", fontSize: 12, fontWeight: 600,
        color: T.muted, letterSpacing: "0.04em",
        textTransform: "uppercase", marginBottom: 7,
      }}>{label}</label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: "100%", boxSizing: "border-box",
          background: T.cardAlt,
          border: `1px solid ${focused ? T.accentBorder : T.border}`,
          borderRadius: 10, padding: "11px 14px",
          fontFamily: "inherit", fontSize: 14,
          color: T.text, outline: "none",
          transition: "border-color 0.15s, background 0.15s",
          ...(focused ? { background: "rgba(13,102,211,0.04)" } : {}),
        }}
      />
      {extra}
    </div>
  );
}

// ─── Phone Input ──────────────────────────────────────────────────────────────
const DIAL_CODES = [
  { code: "+1",   flag: "🇺🇸", label: "US" },
  { code: "+44",  flag: "🇬🇧", label: "UK" },
  { code: "+91",  flag: "🇮🇳", label: "IN" },
  { code: "+971", flag: "🇦🇪", label: "AE" },
  { code: "+61",  flag: "🇦🇺", label: "AU" },
  { code: "+49",  flag: "🇩🇪", label: "DE" },
  { code: "+33",  flag: "🇫🇷", label: "FR" },
];

function PhoneInput({ value, onChange, dialCode, onDialChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: "block", fontSize: 12, fontWeight: 600,
        color: T.muted, letterSpacing: "0.04em",
        textTransform: "uppercase", marginBottom: 7,
      }}>Phone number</label>
      <div style={{
        display: "flex",
        border: `1px solid ${focused ? T.accentBorder : T.border}`,
        borderRadius: 10, overflow: "hidden",
        background: focused ? "rgba(13,102,211,0.04)" : T.cardAlt,
        transition: "border-color 0.15s, background 0.15s",
      }}>
        <select
          value={dialCode}
          onChange={e => onDialChange(e.target.value)}
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "none",
            borderRight: `1px solid ${T.border}`,
            color: T.text, fontSize: 13, fontWeight: 500,
            padding: "11px 10px",
            cursor: "pointer", outline: "none",
            fontFamily: "inherit", flexShrink: 0,
          }}
        >
          {DIAL_CODES.map(d => (
            <option key={d.code} value={d.code} style={{ background: "#111827" }}>
              {d.flag} {d.code}
            </option>
          ))}
        </select>
        <input
          type="tel"
          placeholder="300 1234567"
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1, background: "transparent", border: "none",
            padding: "11px 14px", fontFamily: "inherit",
            fontSize: 14, color: T.text, outline: "none",
          }}
        />
      </div>
    </div>
  );
}

// ─── Primary Button ───────────────────────────────────────────────────────────
function PrimaryBtn({ children, onClick, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "100%",
        background: disabled
          ? "rgba(255,255,255,0.05)"
          : "linear-gradient(135deg, #0D66D3, #1C8BFA)",
        border: "none", borderRadius: 10, padding: "13px",
        fontFamily: "inherit", fontSize: 14, fontWeight: 700,
        color: disabled ? T.faint : "#fff",
        cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: (!disabled && hovered)
          ? "0 6px 22px rgba(28,139,250,0.45)"
          : "0 4px 16px rgba(13,102,211,0.35)",
        transform: (!disabled && hovered) ? "translateY(-1px)" : "translateY(0)",
        transition: "all 0.18s",
        marginBottom: 18, letterSpacing: "0.01em",
      }}
    >{children}</button>
  );
}

// ─── Auth Card ────────────────────────────────────────────────────────────────
function AuthCard({ children }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.borderMid}`,
      borderRadius: 20, padding: "40px 44px",
      width: "100%", maxWidth: 440, position: "relative",
    }}>
      <div style={{
        position: "absolute", inset: 0, borderRadius: 20,
        background: "linear-gradient(135deg, rgba(13,102,211,0.05) 0%, transparent 60%)",
        pointerEvents: "none",
      }} />
      {children}
    </div>
  );
}

// ─── Card Tag ─────────────────────────────────────────────────────────────────
function CardTag({ children, color, bgColor, borderColor }) {
  const c = color || T.accent;
  const bg = bgColor || T.accentDim;
  const border = borderColor || T.accentBorder;
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: bg, border: `1px solid ${border}`,
      borderRadius: 20, padding: "4px 12px",
      fontSize: 11, fontWeight: 600, color: c,
      letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 14,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c, display: "inline-block" }} />
      {children}
    </div>
  );
}

// ─── Back Button ──────────────────────────────────────────────────────────────
function BackBtn({ onClick, label = "Back" }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: "none", border: "none", padding: 0,
        color: hovered ? T.accent : T.muted,
        fontSize: 13, fontWeight: 500,
        cursor: "pointer", fontFamily: "inherit",
        transition: "color 0.15s",
        marginBottom: 20,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M9 11L5 7L9 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {label}
    </button>
  );
}

// ─── OTP Digit Input ──────────────────────────────────────────────────────────
function OTPInput({ value, onChange, onSubmit }) {
  const refs = useRef([]);
  const digits = value.split("").concat(Array(6).fill("")).slice(0, 6);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        const next = [...digits]; next[i] = ""; onChange(next.join(""));
      } else if (i > 0) {
        refs.current[i - 1]?.focus();
        const next = [...digits]; next[i - 1] = ""; onChange(next.join(""));
      }
      return;
    }
    if (e.key === "Enter" && value.length === 6) { onSubmit(); return; }
    if (!/^\d$/.test(e.key)) return;
    const next = [...digits]; next[i] = e.key; onChange(next.join(""));
    if (i < 5) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (text) { onChange(text.padEnd(6, "").slice(0, 6)); refs.current[Math.min(text.length, 5)]?.focus(); }
    e.preventDefault();
  };

  return (
    <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 24 }}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => refs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          onChange={() => {}}
          onClick={() => refs.current[i]?.select()}
          style={{
            width: 50, height: 58,
            background: d ? "rgba(13,102,211,0.08)" : T.cardAlt,
            border: `1.5px solid ${d ? T.accentBorder : T.border}`,
            borderRadius: 12,
            textAlign: "center",
            fontSize: 22, fontWeight: 700,
            color: T.text, fontFamily: "inherit",
            outline: "none",
            transition: "all 0.15s",
            caretColor: "transparent",
          }}
        />
      ))}
    </div>
  );
}

// ─── Countdown Timer ──────────────────────────────────────────────────────────
function CountdownTimer({ seconds, onExpire }) {
  const [remaining, setRemaining] = useState(seconds);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    setRemaining(seconds);
  }, [seconds]);

  useEffect(() => {
    if (remaining <= 0) { onExpireRef.current?.(); return; }
    const id = setTimeout(() => setRemaining(r => r - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining]);

  const m = String(Math.floor(remaining / 60)).padStart(2, "0");
  const s = String(remaining % 60).padStart(2, "0");
  const pct = remaining / seconds;
  const color = remaining <= 60 ? T.red : remaining <= 120 ? T.orange : T.accent;
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const dash = circumference * pct;

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: 24,
    }}>
      <div style={{ position: "relative", width: 72, height: 72 }}>
        {/* Track */}
        <svg width="72" height="72" style={{ position: "absolute", top: 0, left: 0, transform: "rotate(-90deg)" }}>
          <circle cx="36" cy="36" r={radius} fill="none" stroke={T.border} strokeWidth="3" />
          <circle
            cx="36" cy="36" r={radius} fill="none"
            stroke={color} strokeWidth="3"
            strokeDasharray={`${dash} ${circumference}`}
            strokeLinecap="round"
            style={{ transition: "stroke-dasharray 0.8s linear, stroke 0.5s" }}
          />
        </svg>
        {/* Time text */}
        <div style={{
          position: "absolute", inset: 0, display: "flex",
          alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 700, color, fontVariantNumeric: "tabular-nums",
          fontFamily: "'SF Mono', 'Fira Code', monospace",
        }}>
          {m}:{s}
        </div>
      </div>
      <span style={{ fontSize: 11, color: T.faint, letterSpacing: "0.04em" }}>
        {remaining > 0 ? "Code expires in" : "Code expired"}
      </span>
    </div>
  );
}

// ─── Forgot Password Page ─────────────────────────────────────────────────────
function ForgotPage({ onCodeSent }) {
  const [email, setEmail]     = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSend = () => {
    if (!email.trim()) { alert("Please enter your email address."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { alert("Please enter a valid email address."); return; }
    setLoading(true);
    // Simulate generating + "sending" a 6-digit OTP
    const code = String(Math.floor(100000 + Math.random() * 900000));
    setTimeout(() => {
      setLoading(false);
      // In production: send code via API. Here we pass it to parent for verification.
      onCodeSent(email, code);
    }, 1200);
  };

  return (
    <AuthCard>
      <BackBtn onClick={() => navigate("/login")} label="Back to login" />

      <div style={{ marginBottom: 28 }}>
        <CardTag>Password recovery</CardTag>
        <h1 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>
          Forgot your password?
        </h1>
        <p style={{ margin: 0, fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
          No worries — enter your email and we'll send you a 6-digit verification code to reset your password.
        </p>
      </div>

      {/* Icon */}
      <div style={{
        width: 64, height: 64, borderRadius: 16, marginBottom: 24,
        background: T.accentDim, border: `1px solid ${T.accentBorder}`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="3" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
      </div>

      <FormInput
        label="Email address"
        type="email"
        placeholder="you@company.com"
        value={email}
        onChange={e => setEmail(e.target.value)}
      />

      <PrimaryBtn onClick={handleSend} disabled={loading}>
        {loading ? "Sending code…" : "Send verification code"}
      </PrimaryBtn>

      <p style={{ textAlign: "center", fontSize: 12, color: T.faint, margin: 0 }}>
        Check your spam folder if you don't see the email
      </p>
    </AuthCard>
  );
}

// ─── OTP Verification Page ────────────────────────────────────────────────────
function OTPPage({ email, correctCode, onResend }) {
  const [otp,     setOtp]     = useState("");
  const [error,   setError]   = useState("");
  const [expired, setExpired] = useState(false);
  const [success, setSuccess] = useState(false);
  const [timerKey, setTimerKey] = useState(0);   // bump to reset timer
  const navigate = useNavigate();
  const OTP_SECONDS = 10 * 60; // 10 minutes

  const handleVerify = () => {
    if (otp.length < 6) { setError("Please enter all 6 digits."); return; }
    if (expired)        { setError("This code has expired. Please request a new one."); return; }
    if (otp !== correctCode) {
      setError("Incorrect code. Please check and try again.");
      setOtp("");
      return;
    }
    setError("");
    setSuccess(true);
    setTimeout(() => navigate("/login"), 2200);
  };

  const handleResend = () => {
    setExpired(false);
    setError("");
    setOtp("");
    setTimerKey(k => k + 1);
    onResend();
  };

  // Reveal code in dev mode (comment out for production)
  const devHint = correctCode;

  if (success) {
    return (
      <AuthCard>
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{
            width: 72, height: 72, borderRadius: "50%", margin: "0 auto 20px",
            background: T.greenDim, border: `1px solid ${T.greenBorder}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.green} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          <CardTag color={T.green} bgColor={T.greenDim} borderColor={T.greenBorder}>Verified!</CardTag>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: "0 0 8px" }}>
            Identity confirmed
          </h2>
          <p style={{ fontSize: 13, color: T.muted, margin: 0 }}>
            Redirecting you to login…
          </p>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <BackBtn onClick={() => navigate("/forgot-password")} label="Back" />

      <div style={{ marginBottom: 24 }}>
        <CardTag>OTP verification</CardTag>
        <h1 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>
          Check your email
        </h1>
        <p style={{ margin: 0, fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
          We sent a 6-digit code to{" "}
          <span style={{ color: T.accent, fontWeight: 600 }}>{email}</span>.
          Enter it below to continue.
        </p>
      </div>

      {/* Dev hint — remove in production */}
      <div style={{
        background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.25)",
        borderRadius: 10, padding: "10px 14px", marginBottom: 20,
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.orange} strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
        </svg>
        <span style={{ fontSize: 12, color: T.orange }}>
          Dev mode — your OTP: <strong style={{ letterSpacing: "0.12em" }}>{devHint}</strong>
        </span>
      </div>

      {/* Countdown timer */}
      <CountdownTimer key={timerKey} seconds={OTP_SECONDS} onExpire={() => setExpired(true)} />

      {/* 6-box OTP input */}
      <OTPInput value={otp} onChange={v => { setOtp(v); setError(""); }} onSubmit={handleVerify} />

      {/* Error */}
      {error && (
        <div style={{
          background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
          borderRadius: 10, padding: "10px 14px", marginBottom: 16,
          fontSize: 13, color: T.red, display: "flex", alignItems: "center", gap: 8,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.red} strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6" />
          </svg>
          {error}
        </div>
      )}

      {/* Expired banner */}
      {expired && (
        <div style={{
          background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)",
          borderRadius: 10, padding: "10px 14px", marginBottom: 16,
          fontSize: 13, color: T.red, textAlign: "center",
        }}>
          ⏰ This code has expired.
        </div>
      )}

      <PrimaryBtn onClick={handleVerify} disabled={otp.length < 6}>
        Verify code
      </PrimaryBtn>

      <p style={{ textAlign: "center", fontSize: 12, color: T.faint, margin: 0 }}>
        Didn't receive a code?{" "}
        <button onClick={handleResend} style={{
          background: "none", border: "none", padding: 0,
          color: T.accent, fontSize: 12, fontWeight: 500,
          cursor: "pointer", fontFamily: "inherit",
        }}>Resend code</button>
      </p>
    </AuthCard>
  );
}

// ─── Login Page ───────────────────────────────────────────────────────────────
function LoginPage() {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const navigate = useNavigate();

  return (
    <AuthCard>
      <div style={{ marginBottom: 28 }}>
        <CardTag>Welcome back</CardTag>
        <h1 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>
          Login in to SportsKit
        </h1>
        <p style={{ margin: 0, fontSize: 13, color: T.muted }}>
          Don't have an account?{" "}
          <button onClick={() => navigate("/signup")} style={{
            background: "none", border: "none", padding: 0,
            color: T.accent, fontSize: 13, fontWeight: 500,
            cursor: "pointer", fontFamily: "inherit",
          }}>Create one free</button>
        </p>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <div style={{ flex: 1, height: 1, background: T.border }} />
        <span style={{ fontSize: 11, color: T.faint, fontWeight: 500, letterSpacing: "0.05em", textTransform: "uppercase" }}>
          Login in with email
        </span>
        <div style={{ flex: 1, height: 1, background: T.border }} />
      </div>

      <FormInput label="Email address" type="email" placeholder="you@company.com"
        value={email} onChange={e => setEmail(e.target.value)} />
      <FormInput label="Password" type="password" placeholder="••••••••"
        value={password} onChange={e => setPassword(e.target.value)} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.muted, cursor: "pointer" }}>
          <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
            style={{ accentColor: T.accent, width: 14, height: 14, cursor: "pointer" }} />
          Remember me
        </label>
        <button
          onClick={() => navigate("/forgot-password")}
          style={{
            background: "none", border: "none", padding: 0,
            color: T.accent, fontSize: 12, fontWeight: 500,
            cursor: "pointer", fontFamily: "inherit", textDecoration: "none",
          }}
        >
          Forgot password?
        </button>
      </div>

      <PrimaryBtn onClick={() => alert(`Signing in as: ${email}`)}>
        Login in to dashboard
      </PrimaryBtn>

      <p style={{ textAlign: "center", fontSize: 12, color: T.faint, margin: 0 }}>
        Protected by enterprise-grade encryption
      </p>
    </AuthCard>
  );
}

// ─── Signup Page ──────────────────────────────────────────────────────────────
function SignupPage() {
  const [firstName, setFirstName] = useState("");
  const [email,     setEmail]     = useState("");
  const [phone,     setPhone]     = useState("");
  const [dialCode,  setDialCode]  = useState("+1");
  const [password,  setPassword]  = useState("");
  const [agreed,    setAgreed]    = useState(false);
  const navigate = useNavigate();
  const strength = getStrength(password);

  const handleSubmit = () => {
    if (!agreed) { alert("Please agree to the Terms of Service."); return; }
    alert(`Account created!\nName: ${firstName}\nEmail: ${email}\nPhone: ${dialCode} ${phone}`);
    navigate("/admin");
  };

  return (
    <AuthCard>
      <div style={{ marginBottom: 28 }}>
        <CardTag>Get started free</CardTag>
        <h1 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>
          Create your account
        </h1>
        <p style={{ margin: 0, fontSize: 13, color: T.muted }}>
          Already have one?{" "}
          <button onClick={() => navigate("/login")} style={{
            background: "none", border: "none", padding: 0,
            color: T.accent, fontSize: 13, fontWeight: 500,
            cursor: "pointer", fontFamily: "inherit",
          }}>Login in here</button>
        </p>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <div style={{ flex: 1, height: 1, background: T.border }} />
        <span style={{ fontSize: 11, color: T.faint, fontWeight: 500, letterSpacing: "0.05em", textTransform: "uppercase" }}>
          Sign up with email
        </span>
        <div style={{ flex: 1, height: 1, background: T.border }} />
      </div>

      <FormInput label="First name" placeholder="Alex"
        value={firstName} onChange={e => setFirstName(e.target.value)} />

      <FormInput label="Email address" type="email" placeholder="you@company.com"
        value={email} onChange={e => setEmail(e.target.value)} />

      <PhoneInput
        value={phone} onChange={e => setPhone(e.target.value)}
        dialCode={dialCode} onDialChange={setDialCode}
      />

      <FormInput
        label="Password" type="password" placeholder="Min. 8 characters"
        value={password} onChange={e => setPassword(e.target.value)}
        extra={password && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={{
                  flex: 1, height: 3, borderRadius: 2,
                  background: i <= strength ? STRENGTH_COLORS[strength] : T.border,
                  transition: "background 0.25s",
                }} />
              ))}
            </div>
            <span style={{ fontSize: 11, color: STRENGTH_COLORS[strength] }}>
              {STRENGTH_LABELS[strength]}
            </span>
          </div>
        )}
      />

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
            style={{ accentColor: T.accent, width: 14, height: 14, marginTop: 2, cursor: "pointer", flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
            I agree to the{" "}
            <a href="/terms" style={{ color: T.accent, textDecoration: "none" }}>Terms of Service</a>
            {" "}and{" "}
            <a href="/privacy" style={{ color: T.accent, textDecoration: "none" }}>Privacy Policy</a>
          </span>
        </label>
      </div>

      <PrimaryBtn onClick={handleSubmit}>Create free account</PrimaryBtn>

      <p style={{ textAlign: "center", fontSize: 12, color: T.faint, margin: 0 }}>
        No credit card required &nbsp;·&nbsp; Free forever plan available
      </p>
    </AuthCard>
  );
}

// ─── App Shell ────────────────────────────────────────────────────────────────
export default function Auth() {
  const location = useLocation();

  // OTP state lifted here so ForgotPage can pass code → OTPPage
  const [otpData, setOtpData] = useState({ email: "", code: "" });

  const path = location.pathname;
  const page = path === "/signup"
    ? "signup"
    : path === "/forgot-password"
      ? "forgot"
      : path === "/verify-otp"
        ? "otp"
        : "login";

  const navigate = useNavigate();

  const handleCodeSent = (email, code) => {
    setOtpData({ email, code });
    navigate("/verify-otp");
  };

  const handleResend = () => {
    const newCode = String(Math.floor(100000 + Math.random() * 900000));
    setOtpData(prev => ({ ...prev, code: newCode }));
  };

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100vh",
      background: T.bg, fontFamily: "system-ui, -apple-system, sans-serif",
      overflow: "hidden", position: "relative",
    }}>
      {/* Background glows */}
      <div style={{
        position: "absolute", width: 600, height: 600, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(13,102,211,0.08) 0%, transparent 70%)",
        top: -200, right: -200, pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", width: 600, height: 600, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(13,102,211,0.05) 0%, transparent 70%)",
        bottom: -200, left: -200, pointerEvents: "none",
      }} />

      <main style={{
        flex: 1, display: "flex", alignItems: "center",
        justifyContent: "center", padding: "40px 20px",
        overflowY: "auto", position: "relative", zIndex: 2,
      }}>
        {page === "login"  && <LoginPage />}
        {page === "signup" && <SignupPage />}
        {page === "forgot" && <ForgotPage onCodeSent={handleCodeSent} />}
        {page === "otp"    && (
          <OTPPage
            email={otpData.email}
            correctCode={otpData.code}
            onResend={handleResend}
          />
        )}
      </main>
    </div>
  );
} 